import { useCallback, useEffect, useState } from "react";
import { BurndownChart, type BurndownSeries } from "./burndown-chart";
import { ErrorBanner, Skeleton } from "./ui-kit";

type SprintOption = { id: string; name: string; state: "active" | "cancelled" | "completed" | "planned" };

type Burndown = {
  byAssignee: Array<{ assigneeId: string | null; assigneeName: string; remainingPoints: number[]; scopePoints: number[] }>;
  days: string[];
  limitations: { columnResolutionGap: boolean; nameCollisionRisk: boolean; startDateInferred: boolean };
  overall: { remainingPoints: number[]; scopePoints: number[]; unestimatedCount: number[] };
  sprint: { endsOn: string | null; id: string; name: string; startsOn: string | null; state: string };
};

const api = async <T,>(path: string): Promise<T> => {
  const response = await fetch(path);
  const payload = (await response.json()) as T & { message?: string };
  if (!response.ok) throw new Error(payload.message || "Taskei could not complete that request.");
  return payload;
};

// A small, self-contained categorical palette for overlaid per-assignee
// lines -- no shared multi-hue palette exists elsewhere in this codebase to
// draw from (styles.css defines semantic tokens like --jira-blue, not a
// categorical set), so this follows the same local-constant pattern
// task-icons.tsx already uses for its own type/priority color maps.
const ASSIGNEE_COLORS = ["#0c66e4", "#e2483d", "#2f8132", "#8f5fe8", "#c9611a", "#1558bc", "#946f00", "#ae2a19"];

export function ReportsPage({ onSelectSprint, sprintId, teamId }: { onSelectSprint: (sprintId: string | null) => void; sprintId: string | null; teamId: string }) {
  const [sprints, setSprints] = useState<SprintOption[] | null>(null);
  const [burndown, setBurndown] = useState<Burndown | null>(null);
  const [byAssignee, setByAssignee] = useState(false);
  const [error, setError] = useState("");

  const loadSprints = useCallback(async () => {
    const result = await api<{ sprints: SprintOption[] }>(`/api/sprints?teamId=${encodeURIComponent(teamId)}`);
    const reportable = result.sprints.filter(({ state }) => state === "active" || state === "completed");
    setSprints(reportable);
    if (!sprintId && reportable.length) {
      const defaultSprint = reportable.find(({ state }) => state === "active") ?? reportable[0];
      onSelectSprint(defaultSprint.id);
    }
  }, [onSelectSprint, sprintId, teamId]);

  useEffect(() => { void loadSprints().catch((reason) => setError(reason instanceof Error ? reason.message : "Could not load sprints.")); }, [loadSprints]);

  useEffect(() => {
    if (!sprintId) { setBurndown(null); return; }
    let cancelled = false;
    setBurndown(null);
    setError("");
    api<Burndown>(`/api/sprints/${encodeURIComponent(sprintId)}/burndown`)
      .then((result) => { if (!cancelled) setBurndown(result); })
      .catch((reason) => { if (!cancelled) setError(reason instanceof Error ? reason.message : "Could not load the burndown report."); });
    return () => { cancelled = true; };
  }, [sprintId]);

  if (!sprints) return error ? <ErrorBanner>{error}</ErrorBanner> : <Skeleton label="Loading reports…" variant="list" />;

  const limitationMessages: string[] = [];
  if (burndown?.limitations.startDateInferred) limitationMessages.push("This sprint has no start date set, so the chart's first day was inferred from its earliest task.");
  if (burndown?.limitations.nameCollisionRisk) limitationMessages.push("Another sprint in this team shares this sprint's name, which can reduce the accuracy of historical reconstruction.");
  if (burndown?.limitations.columnResolutionGap) limitationMessages.push("Some historical board-column moves reference a column that's since been renamed or deleted, which can reduce accuracy for those transitions.");

  const idealTrend = burndown ? burndown.days.map((_, index) =>
    burndown.days.length <= 1 ? burndown.overall.scopePoints[0] ?? 0
      : Math.max(0, (burndown.overall.scopePoints[0] ?? 0) * (1 - index / (burndown.days.length - 1))),
  ) : [];

  const overallSeries: BurndownSeries[] = burndown ? [
    { color: "#6b778c", dashed: true, id: "ideal", label: "Ideal trend", values: idealTrend },
    { color: "#0c66e4", id: "remaining", label: "Points remaining", values: burndown.overall.remainingPoints },
  ] : [];

  const assigneeSeries: BurndownSeries[] = burndown
    ? burndown.byAssignee.map((row, index) => ({
        color: ASSIGNEE_COLORS[index % ASSIGNEE_COLORS.length],
        id: row.assigneeId ?? "unassigned",
        label: row.assigneeName,
        values: row.remainingPoints,
      }))
    : [];

  const totalUnestimated = burndown ? Math.max(...burndown.overall.unestimatedCount, 0) : 0;

  return (
    <section className="reports-page">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Delivery reports</p>
          <h2>Sprint burndown</h2>
          <p className="section-description">Story points remaining over time, reconstructed from each task's edit history.</p>
        </div>
      </div>
      <ErrorBanner>{error}</ErrorBanner>
      <div className="reports-controls">
        <label>
          Sprint
          <select onChange={(event) => onSelectSprint(event.target.value || null)} value={sprintId ?? ""}>
            {!sprints.length && <option value="">No active or completed sprints yet</option>}
            {sprints.map((sprint) => <option key={sprint.id} value={sprint.id}>{sprint.name}{sprint.state === "active" ? " (active)" : ""}</option>)}
          </select>
        </label>
        <label className="reports-assignee-toggle">
          <input checked={byAssignee} onChange={(event) => setByAssignee(event.target.checked)} type="checkbox" />
          Split by assignee
        </label>
      </div>
      {!sprints.length ? (
        <div className="empty-state compact"><h2>No sprint to report on yet</h2><p>Start or complete a sprint to see its burndown here.</p></div>
      ) : !burndown ? (
        <Skeleton label="Loading burndown…" variant="list" />
      ) : (
        <>
          {limitationMessages.length > 0 && (
            <div className="reports-limitations-banner" role="status">
              {limitationMessages.map((message) => <p key={message}>{message}</p>)}
            </div>
          )}
          <dl className="sprint-summary">
            <div><dt>Scope</dt><dd>{burndown.overall.scopePoints[burndown.overall.scopePoints.length - 1] ?? 0} pts</dd></div>
            <div><dt>Remaining</dt><dd>{burndown.overall.remainingPoints[burndown.overall.remainingPoints.length - 1] ?? 0} pts</dd></div>
            {totalUnestimated > 0 && <div><dt>Unestimated</dt><dd>{totalUnestimated} task{totalUnestimated === 1 ? "" : "s"}</dd></div>}
          </dl>
          <BurndownChart days={burndown.days} series={byAssignee ? assigneeSeries : overallSeries} />
        </>
      )}
    </section>
  );
}
